<script setup lang="ts">
import { computed } from 'vue';
import { ArrowLeft, X, ArrowUp, User, Maximize2, BookOpen } from 'lucide-vue-next';

const props = defineProps<{
  typedText: string;
}>();

const results = computed(() => {
  const text = props.typedText.toLowerCase();
  
  if (text.startsWith('arsenal live')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal Liverpool', meta: '', icon: User },
      { id: 2, type: 'match', text: 'Arsenal Liverpool - Villa', meta: 'Fri 20:00', icon: 'pi pi-calendar' }
    ];
  }

  // --- Spain to win Yamal to score or assist ---
  if (text.startsWith('spain to win yamal to score or')) {
    return [
      { id: 1, type: 'market', text: '...win Yamal To Score or Assist', meta: '', icon: BookOpen },
      { id: 2, type: 'player', text: '... to score Oroz', meta: '', icon: User },
      { id: 3, type: 'player', text: '... to score Ortiz', meta: '', icon: User },
      { id: 4, type: 'player', text: '... to score Orford', meta: '', icon: User },
      { id: 5, type: 'player', text: '... to score Ortega', meta: '', icon: User }
    ];
  }

  if (text.startsWith('spain to win yamal to sco')) {
    return [
      { id: 1, type: 'market', text: '...win Yamal To Score', meta: '', icon: BookOpen },
      { id: 2, type: 'player', text: '... Yamal to Scott', meta: '', icon: User },
      { id: 3, type: 'player', text: '... Yamal to Scott Twine', meta: '', icon: User },
      { id: 4, type: 'player', text: '... Yamal to Scott Wright', meta: '', icon: User }
    ];
  }

  if (text.startsWith('spain to win yamal')) {
    return [
      { id: 1, type: 'player', text: '...in to win Yamal', meta: '', icon: User }
    ];
  }

  if (text.startsWith('spain')) {
    return [
      { id: 1, type: 'team', text: 'Spain', meta: '', icon: User },
      { id: 2, type: 'match', text: 'Spain - Austria', meta: 'Thu 20:00', icon: 'pi pi-calendar' }
    ];
  }

  // --- Chelsea to win over 2 goals both teams to score ---
  if (text.startsWith('chelsea to win over 2 goals both teams to')) {
    return [
      { id: 1, type: 'market', text: '...r 2 goals Both Teams to Score', meta: '', icon: BookOpen },
      { id: 2, type: 'player', text: '...oth teams Toal', meta: '', icon: User },
      { id: 3, type: 'player', text: '...oth teams Todd', meta: '', icon: User },
      { id: 4, type: 'player', text: '...oth teams Topp', meta: '', icon: User }
    ];
  }

  if (text.startsWith('chelsea to win over 2 goals')) {
    return [
      { id: 1, type: 'market', text: '...in over 2 Goals', meta: '', icon: BookOpen }
    ];
  }

  if (text.startsWith('chelsea to win')) {
    return [
      { id: 1, type: 'market', text: 'Chelsea To Win', meta: '', icon: BookOpen },
      { id: 2, type: 'player', text: '...helsea to Wintle', meta: '', icon: User },
      { id: 3, type: 'player', text: '...helsea to Windass', meta: '', icon: User }
    ];
  }

  if (text.startsWith('chel')) {
    return [
      { id: 1, type: 'team', text: 'Chelsea', meta: '', icon: User },
      { id: 2, type: 'match', text: 'Chelsea - Fulham', meta: 'Mon 24th Aug 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  if (text.startsWith('arsenal')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal', meta: '', icon: User },
      { id: 3, type: 'match', text: 'Arsenal - PSG', meta: 'Sat 30th 17:00', icon: 'pi pi-calendar' },
      { id: 4, type: 'match', text: 'Arsenal - Burnley', meta: 'Mon 18th 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  if (text.startsWith('arse')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal', meta: '', icon: User },
      { id: 2, type: 'player', text: 'Arsen Zakharyan', meta: '', icon: User },
      { id: 3, type: 'match', text: 'Arsenal - PSG', meta: 'Sat 30th 17:00', icon: 'pi pi-calendar' },
      { id: 4, type: 'match', text: 'Arsenal - Burnley', meta: 'Mon 18th 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  return [];
});

const formatText = (fullText: string) => {
  const input = props.typedText;
  if (!fullText.toLowerCase().startsWith(input.toLowerCase())) {
    return { typed: '', suggested: fullText };
  }
  return {
    typed: fullText.slice(0, input.length),
    suggested: fullText.slice(input.length)
  };
};
</script>

<template>
  <div class="insight-modal-full">
    <div class="insight-header">
      <button class="i-back"><ArrowLeft :size="20" /></button>
      <div class="i-input-mock">
        <span>{{ typedText }}</span>
        <span class="blink-cursor">|</span>
      </div>
      <div class="i-actions">
        <X :size="20" class="text-gray-600" />
        <button class="i-enter-btn">
          <ArrowUp :size="16" />
        </button>
      </div>
    </div>
    
    <div class="insight-results">
      <div v-for="(res, idx) in results" :key="res.id" class="i-res-row" :class="{ active: idx === 0 }">
        <div class="i-res-icon">
          <component v-if="typeof res.icon !== 'string'" :is="res.icon" :size="20" />
          <i v-else :class="res.icon"></i>
        </div>
        
        <div class="i-res-text">
          <div class="i-res-title-wrap">
            <span class="i-typed">{{ formatText(res.text).typed }}</span>
            <span class="i-suggested">{{ formatText(res.text).suggested }}</span>
          </div>
          <span v-if="res.meta" class="i-res-meta">{{ res.meta }}</span>
        </div>
        
        <div class="i-res-actions">
          <Maximize2 v-if="res.type === 'team' || res.type === 'player'" :size="16" class="i-icon-dim" />
          <div class="i-arrow-pair">
            <i class="pi pi-arrow-up-left i-icon-dim"></i>
            <ArrowUp :size="16" class="i-icon-dim" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.insight-modal-full { position: absolute; inset: 0; background: #111113; z-index: 150; padding-top: 40px; }
.insight-header { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.i-back { background: transparent; border: none; color: #fff; }
.i-input-mock { flex: 1; color: #fff; font-size: 13px; font-weight: 400; display: flex; align-items: center; }
.blink-cursor { color: #3b82f6; font-weight: 100; margin-left: 2px; animation: blink 1s infinite; }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
.i-actions { display: flex; align-items: center; gap: 10px; }
.i-enter-btn { background: #e21b0d; border: none; border-radius: 50%; width: 28px; height: 28px; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; }

.insight-results { padding: 4px 0; }
.i-res-row { display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.02); }
.i-res-row.active { background: rgba(255,255,255,0.03); }
.i-res-icon { color: #777; width: 20px; display: flex; justify-content: center; }
.i-res-text { flex: 1; display: flex; flex-direction: column; }

.i-res-title-wrap { font-size: 13px; color: #fff; display: flex; white-space: pre; font-style: italic; }
.i-typed { font-weight: 400; opacity: 0.8; }
.i-suggested { font-weight: 800; }

.i-res-meta { font-size: 11px; color: #555; margin-top: 1px; }
.i-res-actions { display: flex; align-items: center; gap: 20px; color: #444; }
.i-arrow-pair { display: flex; gap: 20px; }
.i-icon-dim { opacity: 0.6; }
</style>
